import { useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { Badge } from '@/common/ui/badge'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { PromotionSelector } from '@/modules/messaging/components/PromotionSelector'
import useMutationUpdateBrokeFlexMessageCampaign from '../../api/hooks/useMutationUpdateBrokeFlexMessageCampaign'

const editStageSchema = z.object({
	minutesAfter: z.number().min(1, 'ต้องมากกว่า 0 นาที'),
	message: z.string().min(1, 'กรุณากรอกข้อความ'),
	type: z.enum(['PROMOTION', 'COUPON', 'INSTANT_CREDIT']),
	promotionId: z.string().optional(),
	couponCode: z.string().optional(),
})

type EditStageFormValues = z.infer<typeof editStageSchema>

interface EditStageModalProps {
	open: boolean
	onClose: () => void
	campaignId: string
	stage: any // The stage data to edit
	stageNumber: number
	onSuccess: () => void
}

const STAGE_TYPE_OPTIONS = [
	{
		value: 'PROMOTION' as const,
		label: '🎁 โปรโมชั่น',
		description: 'แสดงโปรโมชั่นฝากเงิน',
	},
	{
		value: 'COUPON' as const,
		label: '🎫 คูปอง',
		description: 'แสดงรหัสคูปองพิเศษ',
	},
	{
		value: 'INSTANT_CREDIT' as const,
		label: '💰 เครดิตทันที',
		description: 'แสดงโปรโมชั่นที่สามารถรับได้ทันที',
	},
]

const SUGGESTED_MINUTES = [5, 10, 30, 60, 120, 360, 1440]

export default function EditStageModal({
	open,
	onClose,
	stage,
	stageNumber,
	onSuccess,
}: EditStageModalProps) {
	// Determine type and item from stage data
	const getTypeAndItem = useCallback(() => {
		if (!stage?.items || stage.items.length === 0) {
			return { type: 'PROMOTION' as const, promotionId: '', couponCode: '' }
		}

		const item = stage.items[0]
		if (item.type === 'DEPOSIT_PROMOTION' || item.promotionId) {
			return {
				type: 'PROMOTION' as const,
				promotionId: item.promotionId || '',
				couponCode: '',
			}
		} else if (item.type === 'COUPON' || item.couponCode) {
			return {
				type: 'COUPON' as const,
				promotionId: '',
				couponCode: item.couponCode || '',
			}
		} else {
			return {
				type: 'INSTANT_CREDIT' as const,
				promotionId: item.promotionId || '',
				couponCode: '',
			}
		}
	}, [stage])

	const initialTypeAndItem = getTypeAndItem()
	const form = useForm<EditStageFormValues>({
		resolver: zodResolver(editStageSchema),
		defaultValues: {
			minutesAfter: stage?.minutesAfterTrigger || 30,
			message: stage?.message || '',
			type: initialTypeAndItem.type,
			promotionId: initialTypeAndItem.promotionId,
			couponCode: initialTypeAndItem.couponCode,
		},
	})

	// Reset form when stage changes
	useEffect(() => {
		if (stage) {
			const typeAndItem = getTypeAndItem()
			form.reset({
				minutesAfter: stage.minutesAfterTrigger,
				message: stage.message,
				type: typeAndItem.type,
				promotionId: typeAndItem.promotionId,
				couponCode: typeAndItem.couponCode,
			})
		}
	}, [stage, form, getTypeAndItem])

	const updateMutation = useMutationUpdateBrokeFlexMessageCampaign({
		onSuccess: () => {
			toast.success('แก้ไขระยะสำเร็จ')
			onSuccess()
			onClose()
		},
		onError: (error) => {
			toast.error('เกิดข้อผิดพลาดในการแก้ไขระยะ')
			console.error('Edit stage error:', error)
		},
	})

	const selectedType = form.watch('type')

	const handleSubmit = async (data: EditStageFormValues) => {
		// Validate required fields based on type
		if (data.type === 'PROMOTION' && !data.promotionId) {
			toast.error('กรุณาเลือกโปรโมชั่น')
			return
		}
		if (data.type === 'COUPON' && !data.couponCode) {
			toast.error('กรุณากรอกรหัสคูปอง')
			return
		}
		if (data.type === 'INSTANT_CREDIT' && !data.promotionId) {
			toast.error('กรุณาเลือกโปรโมชั่น Instant Claim')
			return
		}

		// TODO: Call update API
		// For now, just show success
		toast.success('แก้ไขระยะสำเร็จ')
		onSuccess()
		onClose()
	}

	const getMinutesLabel = (minutes: number) => {
		if (minutes < 60) return `${minutes} นาที`
		if (minutes < 1440) return `${Math.floor(minutes / 60)} ชั่วโมง`
		return `${Math.floor(minutes / 1440)} วัน`
	}

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						แก้ไขระยะที่ {stageNumber}
						<Badge variant="outline">แก้ไขและอัพเดท</Badge>
					</DialogTitle>
					<DialogDescription>
						แก้ไขการตั้งค่าการส่งข้อความสำหรับระยะนี้
					</DialogDescription>
				</DialogHeader>

				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(handleSubmit)}
						className="space-y-6"
					>
						{/* Timing */}
						<FormField
							control={form.control}
							name="minutesAfter"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>ส่งหลังจาก (นาที)</FormLabel>
									<div className="space-y-3">
										<div className="flex flex-wrap gap-2">
											{SUGGESTED_MINUTES.map((minutes) => (
												<Button
													key={minutes}
													type="button"
													variant={
														field.value === minutes ? 'default' : 'outline'
													}
													size="sm"
													onClick={() => field.onChange(minutes)}
												>
													{getMinutesLabel(minutes)}
												</Button>
											))}
										</div>
										<FormControl>
											<Input
												type="number"
												{...field}
												onNumberChange={field.onChange}
												min={1}
												placeholder="หรือกรอกจำนวนนาทีเอง"
											/>
										</FormControl>
									</div>
									<FormDescription>
										ระบบจะส่งข้อความหลังจากผู้เล่นเงินหมดและยังไม่ฝากเงิน{' '}
										{field.value ? getMinutesLabel(field.value) : ''}
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* Message */}
						<FormField
							control={form.control}
							name="message"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>ข้อความหัวข้อ</FormLabel>
									<FormControl>
										<Textarea
											{...field}
											rows={2}
											placeholder="กรอกข้อความที่ต้องการแสดง"
										/>
									</FormControl>
									<FormDescription>
										ข้อความหัวข้อที่จะแสดงด้านบนของการ์ด (พื้นหลังสีแดง)
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* Type Selection */}
						<FormField
							control={form.control}
							name="type"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>ประเภทรายการ</FormLabel>
									<div className="grid gap-3 sm:grid-cols-3">
										{STAGE_TYPE_OPTIONS.map((option) => (
											<button
												key={option.value}
												type="button"
												onClick={() => field.onChange(option.value)}
												className={`rounded-lg border-2 p-4 text-left transition-all hover:border-primary ${
													field.value === option.value
														? 'border-primary bg-primary/5'
														: 'border-border'
												}`}
											>
												<div className="mb-1 text-base font-semibold">
													{option.label}
												</div>
												<div className="text-sm text-muted-foreground">
													{option.description}
												</div>
											</button>
										))}
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* Conditional Fields Based on Type */}
						{selectedType === 'PROMOTION' && (
							<FormField
								control={form.control}
								name="promotionId"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>เลือกโปรโมชั่น</FormLabel>
										<div className="mb-3 rounded-lg border border-blue-200 p-3">
											<p className="text-sm">
												<span className="font-semibold">💡 คำแนะนำ:</span>{' '}
												โปรโมชั่นควรเป็นแบบ <strong>แคมเปญ</strong>{' '}
												เพื่อให้จัดการได้ง่าย
											</p>
										</div>
										<FormControl>
											<PromotionSelector
												value={field.value || ''}
												onChange={field.onChange}
												filterInstantClaimOnly={false}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						{selectedType === 'COUPON' && (
							<FormField
								control={form.control}
								name="couponCode"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>รหัสคูปอง</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder="กรอกรหัสคูปอง เช่น VIP100"
											/>
										</FormControl>
										<FormDescription>
											รหัสคูปองที่ผู้เล่นสามารถใช้ได้
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						{selectedType === 'INSTANT_CREDIT' && (
							<FormField
								control={form.control}
								name="promotionId"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>
											เลือกโปรโมชั่น (Instant Claim)
										</FormLabel>
										<div className="mb-3 rounded-lg border border-blue-200 p-3">
											<p className="text-sm">
												<span className="font-semibold">💡 คำแนะนำ:</span>{' '}
												จะแสดงเฉพาะโปรโมชั่นที่เปิดให้รับได้ทันที
											</p>
										</div>
										<FormControl>
											<PromotionSelector
												value={field.value || ''}
												onChange={field.onChange}
												filterInstantClaimOnly={true}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={onClose}
								disabled={updateMutation.isPending}
							>
								ยกเลิก
							</Button>
							<Button type="submit" disabled={updateMutation.isPending}>
								{updateMutation.isPending ? 'กำลังบันทึก...' : 'บันทึกการแก้ไข'}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
